import { Link, createFileRoute } from "@tanstack/react-router";
import Footer from "#/components/footer/footer";
import NavBar from "#/components/navbar/navbar";
import { organization } from "#/lib/jsonLdSchema";
import { toAppPath } from "#/lib/router-links";
import { metadataToHead } from "#/lib/seo";
import SearchBar from "#/components/searchbar";

export default function HomePage() {
  return (
    <>
      <header>
        <NavBar currentPath="/home" />
      </header>

      <main>
        <div className="bg-blue-50 py-10">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 items-center gap-x-8 gap-y-10 lg:grid-cols-[3fr_2fr]">
              <div>
                <div className="mb-4 text-lg md:text-xl">
                  Find Your Dream Team
                </div>
                <h1 className="mb-4 text-2xl font-bold md:text-3xl lg:text-7xl">
                  Job Search Site for Physicians in Anesthesiology
                </h1>
                <p className="text-base md:text-lg">
                  Discover your next opportunity in the field of anesthesiology
                  with our specialized job search site tailored for healthcare
                  professionals. Explore a wide range of positions across
                  hospitals, clinics, and private practices to find the perfect
                  match for your expertise and career goals.
                </p>
              </div>
              <div className="relative">
                <div className="relative mb-4 overflow-hidden rounded-lg lg:mb-0">
                  <img
                    src="https://roamdoc.s3.us-east-1.amazonaws.com/images/roamdoc-healthcare-lifestyle.webp"
                    alt="Women in healthcare lifestyle setting"
                    width={645}
                    height={1044}
                    className="h-auto w-full object-cover"
                  />
                  <img
                    src="https://roamdoc.s3.us-east-1.amazonaws.com/images/roamdoc-healthcare-job-seeker.webp"
                    alt="Healthcare job seeker"
                    width={390}
                    height={390}
                    className="absolute left-1/4 top-10 w-1/2"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <section className="mx-auto max-w-4xl">
          <SearchBar value="" />
        </section>

        <section className="mx-auto my-10 max-w-4xl px-5">
          <h2 className="mb-4 text-xl font-bold md:text-3xl lg:text-4xl">
            Healthcare Job Seeker Tools & Resources
          </h2>
          <p>
            Explore our comprehensive suite of tools and resources designed to
            empower healthcare professionals in their job search, from
            personalized job alerts to resume-building guidance, ensuring you
            find the right opportunity in the healthcare industry.
          </p>

          <ul>
            <li>
              <Link {...toAppPath("/anesthesiology-job-search")}>
                <h3>Anesthesiology</h3>
              </Link>
            </li>
            <li>
              <h3>Physicians</h3>
            </li>
            <li>
              <h3>Nurses</h3>
            </li>
            <li>
              <h3>Technicians</h3>
            </li>
            <li>
              <h3>Medical Assistants</h3>
            </li>
            <li>
              <h3>Upload Resume</h3>
            </li>
          </ul>
        </section>

        <section className="mx-auto my-10 max-w-4xl px-5">
          <h2 className="mb-4 text-xl font-bold md:text-3xl lg:text-4xl">
            Healthcare Employer Tools
          </h2>

          <p>
            Leverage our advanced suite of tools designed specifically for
            healthcare employers to streamline the hiring process. From posting
            job openings to managing applications, our platform offers a
            seamless, efficient way to connect with top healthcare talent.
          </p>
          <ul>
            <li>
              <Link {...toAppPath("/recruit-anesthesia-providers")}>
                <h3>Recruit Anesthesia Providers</h3>
              </Link>

              <p>Access the best providers in anesthesiology.</p>
            </li>
            <li>
              <h3>Employer Dashboard</h3>
              <p>
                Access real-time insights on job postings, applicant tracking,
                and recruitment analytics.
              </p>
            </li>
            <li>
              <h3>Job Posting Tools</h3>
              <p>
                Utilize our intuitive job posting interface to reach qualified
                candidates across various healthcare specialties.
              </p>
            </li>
            <li>
              <h3>Candidate Search</h3>
              <p>
                Explore our extensive database of healthcare professionals to
                find the perfect match for your open positions.
              </p>
            </li>
            <li>
              <h3>Recruitment Marketing</h3>
              <p>
                Boost your job visibility and employer brand with our targeted
                recruitment marketing solutions.
              </p>
            </li>
            <li>
              <h3>Onboarding Solutions</h3>
              <p>
                Simplify the new hire onboarding process with digital tools
                designed for healthcare settings.
              </p>
            </li>
          </ul>
        </section>

        <section className="mx-auto my-10 max-w-4xl px-5">
          <h2 className="mb-4 text-xl font-bold md:text-3xl lg:text-4xl">
            Medical Centers Hiring Now
          </h2>

          <ul>
            <li>
              <h3>Center for Advanced Medicine</h3>
              <p>
                Now hiring across multiple departments, including oncology,
                cardiology, and pediatric care.
              </p>
            </li>
            <li>
              <h3>Regional Health System</h3>
              <p>
                Seeking experienced nurses, physicians, and support staff to
                join our expanding network.
              </p>
            </li>
            <li>
              <h3>University Medical Center</h3>
              <p>
                Open positions in research, clinical care, and administrative
                roles. Explore opportunities to contribute to groundbreaking
                medical advancements.
              </p>
            </li>
            <li>
              <h3>Community Health Clinics</h3>
              <p>
                Join our team of dedicated healthcare professionals providing
                essential care to underserved communities.
              </p>
            </li>
            <li>
              <h3>Specialty Surgical Centers</h3>
              <p>
                Recruiting specialists in surgery, anesthesia, and perioperative
                care. Be part of a team delivering high-quality, specialized
                surgical services.
              </p>
            </li>
          </ul>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={addJsonLd()}
      />
    </>
  );
}

export const metadata = {
  title: "Search Physician Jobs in Anesthesiology Near You",
  description:
    "Find Physician Anesthesiologist jobs and hospitals hiring in the Texas, Florida, California, New York and all of U.S.",
  icons: [{ rel: "icon", url: "/favicon.ico" }],
  openGraph: {
    images: ["https://www.roamdoc.com/og-image.png"],
  },
  alternates: {
    canonical: `https://www.roamdoc.com`,
  },
};

function addJsonLd() {
  return {
    __html: `{
      "@context": "https://schema.org/",
      "@type": "WebSite",
      "@id": "https://www.roamdoc.com/#WebSite",
      "url": "https://www.roamdoc.com",
      "logo": "hhttps://www.roamdoc.com/roamdoc-logo.png",
      "inLanguage": "en-US",
      "name": "Roamdoc",
      "description": "Job search site for physicians in anesthesiology.",
      "mainEntityOfPage": ${JSON.stringify(organization())},
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": "https://www.roamdoc.com/healthcare-job-search?q={search_term_string}"
        },
        "query-input": "required name=search_term_string"
      },
      "location": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "US"
        }
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "Customer Support",
        "url": "https://www.roamdoc.com/contact-roamdoc",
        "areaServed": "US",
        "availableLanguage": ["English", "Spanish"]
      },
      "copyrightYear": ${new Date().getFullYear()},
      "copyrightHolder": ${JSON.stringify(organization())}
    }`,
  };
}

export const Route = createFileRoute("/")({
  component: HomePage,
  head: () => metadataToHead(metadata),
});
